import { useTranslation } from "react-i18next";
import { cn } from "@/shared/lib/cn";
import { levelTone, riToLevel, RI_BANDS, type Level } from "@/shared/lib/levelTone";

/**
 * RiskBar — 위험도(0~1)가 **어느 등급 구간에 있는지** 한눈에 (2026-09-24 지시).
 *
 * 「0.38」 숫자만으로는 주의인지 경계인지, 경계선까지 얼마나 남았는지 알 수 없다. 트랙을
 * 위험 엔진과 같은 경계(0.30 주의 · 0.50 경계 · 0.70 심각 — `riToLevel`)로 네 칸 칠하고,
 * 현재 값만큼 그 등급 색으로 채운다. `compare` 를 주면 두 번째 표식(예: 발령 당시)을 얇게 긋는다.
 *
 * 두 가지 모양(2026-09-29 관제 개편 A3):
 *   · `fill`(기본) — 값만큼 채운다. 경보 상세·피드·목록은 그대로 이것을 쓴다.
 *   · `marker` — 채우지 않고 **위치 표식**(삼각형 + 2px 선)만 세운다. 관제의 평상값 0.05 는
 *     채움으로 그리면 5% 짜리 초록 부스러기라 「어디쯤인가」가 안 읽혔다. 표식은 값이 바뀌면
 *     `left` 전환으로 미끄러진다(움직임을 줄인 설정은 global.css 가 전환을 끈다).
 * 선·표식·채움은 `-line` 토큰 — 라이트에서 기본 등급색은 흰 바탕 1.5~2.2:1 이라 안 보였다.
 *
 * 색만으로 구분하지 않는다 — 옆에 등급 글자와 숫자가 늘 같이 나온다(색각 보정 팔레트 공통).
 */

/** 트랙 두께·표식 치수(px). xs 는 가스 칸 밑의 3px 줄 — 삼각형 없이 짧은 막대만. */
const DIM = {
  xs: { track: 3, over: 2, tri: 0 },
  sm: { track: 6, over: 3, tri: 4 },
  md: { track: 10, over: 4, tri: 6 },
} as const;

export interface RiskBarProps {
  /** 0~1. null 이면 「값 없음」으로 빈 트랙만 그린다. */
  value: number | null | undefined;
  /** 비교용 두 번째 값(예: 발령 당시). 얇은 세로선으로 표시. */
  compare?: number | null | undefined;
  compareLabel?: string | undefined;
  /** 등급을 값 대신 강제로(예: 서버가 준 경보 등급). 없으면 값으로 계산. */
  level?: Level | undefined;
  /** 앞 글자(예: 「발령 시」「현재」). */
  label?: string | undefined;
  /** xs = 가스 칸 밑 3px 줄, sm = 목록 한 줄, md = 상세(구간 이름 표시). */
  size?: "xs" | "sm" | "md";
  /** fill = 값만큼 채움(기본), marker = 구간 위 위치 표식. */
  variant?: "fill" | "marker";
  /** 0.30·0.50·0.70 눈금. 기본: marker 이고 xs 가 아니면 켬. */
  ticks?: boolean | undefined;
  /** 멈춘 값(「갱신 없음」) — 트랙을 흐리게. 값은 호출 쪽이 null 로 넘긴다(멈춘 값을 지금 값처럼 세우지 않는다). */
  stale?: boolean | undefined;
  /** 숫자 옆 등급 글자 — 같은 행에 등급 칩이 이미 있으면 끈다. */
  showLevelText?: boolean;
  /** 막대만 — 숫자를 이미 크게 보여 주는 곳(관제 KPI 타일). */
  bare?: boolean;
  className?: string | undefined;
}

export function RiskBar({
  value, compare, compareLabel, level, label, size = "sm", variant = "fill", ticks, stale = false,
  showLevelText = true, bare = false, className,
}: RiskBarProps) {
  const { t } = useTranslation();
  const v = value == null || Number.isNaN(value) ? null : Math.max(0, Math.min(1, value));
  const lv: Level = level ?? riToLevel(v ?? 0);
  const tone = levelTone(lv);
  const c = compare == null || Number.isNaN(compare) ? null : Math.max(0, Math.min(1, compare));
  const marker = variant === "marker";
  const showTicks = ticks ?? (marker && size !== "xs");
  const dim = DIM[size];
  // 표식 색은 **구간** 색이다 — 초록 구간 위에 빨간 표식이 서면 위치와 색이 서로 다른 말을 한다.
  // 채움은 종전대로 `level` 강제값을 따른다(경보 상세의 발령 등급).
  const markTone = levelTone(marker ? riToLevel(v ?? 0) : lv);
  // 양 끝에서 삼각형이 잘리지 않게 살짝 안쪽으로.
  const pos = v == null ? null : Math.max(0.5, Math.min(99.5, v * 100));

  return (
    <div className={cn("min-w-0", className)}>
      {!bare && (
      <div className="flex items-baseline gap-2 text-[12px] leading-tight">
        {label && <span className="shrink-0 text-[var(--fg-subtle)]">{label}</span>}
        <span className="font-semibold tabular-nums" style={{ color: `var(${tone.fg})` }}>
          {v == null ? "—" : v.toFixed(2)}
        </span>
        {/* 값도 강제 등급도 없으면 등급 글자를 쓰지 않는다 — 「— 정상」은 값이 없는 것을 안전으로 읽힌다. */}
        {showLevelText && (v != null || level != null) && (
          <span className="font-medium" style={{ color: `var(${tone.fg})` }}>{t(tone.labelKey)}</span>
        )}
        {c != null && compareLabel && (
          <span className="ml-auto shrink-0 text-[11px] text-[var(--fg-subtle)] tabular-nums">
            {compareLabel} {c.toFixed(2)}
          </span>
        )}
      </div>
      )}
      <div
        role="meter"
        aria-valuemin={0}
        aria-valuemax={1}
        aria-valuenow={v ?? undefined}
        aria-valuetext={v == null ? t("widgets.alerts.v2.noValue") : `${v.toFixed(2)} ${t(tone.labelKey)}`}
        className={cn("relative w-full", !bare && "mt-1", stale && "opacity-50")}
        // 표식·눈금이 트랙 위아래로 삐져나올 자리 — 트랙은 안쪽에서 둥글게 잘린다.
        style={marker || showTicks ? { paddingTop: dim.over + dim.tri, paddingBottom: dim.over } : undefined}
      >
        <div className="relative overflow-hidden rounded-full" style={{ height: dim.track }}>
          {RI_BANDS.map((b) => (
            <span
              key={b.level}
              aria-hidden
              className="absolute inset-y-0"
              style={{
                left: `${b.from * 100}%`,
                width: `${(b.to - b.from) * 100}%`,
                background: `var(${levelTone(b.level).soft})`,
              }}
            />
          ))}
          {!marker && v != null && (
            <span
              aria-hidden
              className="absolute inset-y-0 left-0 rounded-full"
              style={{ width: `${Math.max(v * 100, 1.5)}%`, background: `var(${tone.line})` }}
            />
          )}
          {c != null && (
            <span
              aria-hidden
              className="absolute inset-y-0 w-[2px] -translate-x-1/2 bg-[var(--fg)] opacity-70"
              style={{ left: `${c * 100}%` }}
            />
          )}
        </div>
        {showTicks &&
          RI_BANDS.slice(1).map((b) => (
            <span
              key={b.level}
              aria-hidden
              className="absolute w-px -translate-x-1/2 bg-[var(--border)]"
              style={{ left: `${b.from * 100}%`, top: dim.tri + dim.over - 2, height: dim.track + 4 }}
            />
          ))}
        {marker && pos != null && (
          <span
            aria-hidden
            className="absolute top-0 bottom-0"
            style={{
              left: `${pos}%`,
              transition: "left var(--motion-slow) var(--ease-out-soft)",
            }}
          >
            {dim.tri > 0 && (
              <span
                className="absolute top-0"
                style={{
                  left: -dim.tri,
                  borderLeft: `${dim.tri}px solid transparent`,
                  borderRight: `${dim.tri}px solid transparent`,
                  borderTop: `${dim.tri}px solid var(${markTone.line})`,
                }}
              />
            )}
            <span
              className="absolute left-[-1px] w-[2px] rounded-[1px]"
              style={{ top: dim.tri, bottom: 0, background: `var(${markTone.line})` }}
            />
          </span>
        )}
      </div>
      {size === "md" && (
        <div className="relative mt-1 h-3.5 text-[10.5px] text-[var(--fg-subtle)]" aria-hidden>
          {RI_BANDS.map((b) => (
            <span
              key={b.level}
              className="absolute -translate-x-1/2 whitespace-nowrap"
              style={{ left: `${((b.from + b.to) / 2) * 100}%` }}
            >
              {t(levelTone(b.level).labelKey)}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
